import type { UngroupElementsRequest } from '@/api/types/group-elements-api'
import { buildStowedElement } from '@/lib/elements/stowed-element-builder'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import {
  buildRestructureBodyAsGroupSurvivor,
  buildUngroupElementsApiBody,
} from '@/lib/groups/build-group-api-payloads'
import { isGroupShape } from '@/lib/groups/group-kind'
import type { StructuralGroupHistoryPersistenceDeps } from '../types/structural-group-history-persistence-deps'
import {
  collectRemoveGroupEntries,
  findAddGroupEntryInHistory,
  resolveGroupChildMemberIds,
  sortMixedGroupChildIds,
} from './group-history-entries'

export function persistUndoGroupLikeUngroup(
  deps: StructuralGroupHistoryPersistenceDeps,
  txnId: string,
  groupId: string
): void {
  const {
    action,
    shapes,
    textBoxes = [],
    projection = IDENTITY_PROJECTION,
    markEdited,
    enqueueUngroup,
    enqueueRestructure,
    enqueueDelete,
    enqueueUpdate,
    warn,
  } = deps

  const addEntry = findAddGroupEntryInHistory(action)
  const childMemberIds = resolveGroupChildMemberIds(action, deps.plan, groupId)
  const orderedChildIds = sortMixedGroupChildIds(childMemberIds, shapes, textBoxes)
  const nestedKind7Ids = orderedChildIds.filter((id) => {
    const s = shapes.find((x) => x.id === id)

    return s ? isGroupShape(s) : false
  })

  if (nestedKind7Ids.length > 0) {
    for (const gid of nestedKind7Ids) {
      try {
        const apiBody = buildRestructureBodyAsGroupSurvivor(shapes, gid, textBoxes, projection)

        if (apiBody && apiBody.length > 0) {
          enqueueRestructure({
            moves: [],
            meta: { historyTxnId: txnId },
            apiBody,
          })
        }
      } catch (e) {
        warn(
          'persistUndoGroupLikeUngroup: failed to build restructure body for nested child group',
          e
        )
      }
    }

    const releasedNonGroupIds = orderedChildIds.filter((id) => !nestedKind7Ids.includes(id))

    for (const id of releasedNonGroupIds) {
      const s = shapes.find((x) => x.id === id)

      if (s) {
        void markEdited([id])
        enqueueUpdate(buildStowedElement(s, undefined, projection), {
          operation: 'update',
          meta: { historyTxnId: txnId },
        })
        continue
      }

      const tb = textBoxes.find((t) => t.id === id)

      if (tb) {
        void markEdited([id])
        enqueueUpdate(buildStowedElement(tb, undefined, projection), {
          operation: 'update',
          meta: { historyTxnId: txnId },
        })
      }
    }

    void markEdited([groupId, ...orderedChildIds])
    enqueueDelete(groupId, { meta: { historyTxnId: txnId } })
    return
  }

  let apiBody: UngroupElementsRequest | undefined

  if (orderedChildIds.length > 0) {
    try {
      apiBody = buildUngroupElementsApiBody(orderedChildIds, shapes, textBoxes, projection)
    } catch (e) {
      warn('persistUndoGroupLikeUngroup: failed to build ungroup API body', e)
      return
    }
  }

  enqueueUngroup({
    groupId,
    parentGroupId: addEntry?.shape.parentGroupId,
    childIds: orderedChildIds,
    insertIndex: addEntry?.historyInsertIndex,
    meta: { historyTxnId: txnId },
    ...(apiBody && apiBody.length > 0 ? { apiBody } : {}),
  })
}

export function persistRedoUngroupLikeExplicit(
  deps: StructuralGroupHistoryPersistenceDeps,
  txnId: string
): void {
  const {
    shapes,
    textBoxes = [],
    projection = IDENTITY_PROJECTION,
    markEdited,
    enqueueUngroup,
    enqueueRestructure,
    enqueueDelete,
    enqueueUpdate,
    warn,
  } = deps

  for (const rm of collectRemoveGroupEntries(deps.action)) {
    const nestedChildGroupIds = rm.ungroupNestedChildGroupIds ?? []
    const releasedNonGroupIds = sortMixedGroupChildIds(
      rm.ungroupReleasedNonGroupChildIds ?? [],
      shapes,
      textBoxes
    )

    if (nestedChildGroupIds.length > 0) {
      for (const childGroupId of nestedChildGroupIds) {
        try {
          const apiBody = buildRestructureBodyAsGroupSurvivor(
            shapes,
            childGroupId,
            textBoxes,
            projection
          )

          if (apiBody && apiBody.length > 0) {
            enqueueRestructure({
              moves: [],
              meta: { historyTxnId: txnId },
              apiBody,
            })
          }
        } catch (e) {
          warn(
            'persistRedoUngroupLikeExplicit: failed to build restructure body for nested child group',
            e
          )
        }
      }

      for (const id of releasedNonGroupIds) {
        const s = shapes.find((x) => x.id === id)

        if (s) {
          void markEdited([id])
          enqueueUpdate(buildStowedElement(s, undefined, projection), {
            operation: 'update',
            meta: { historyTxnId: txnId },
          })
          continue
        }

        const tb = textBoxes.find((t) => t.id === id)

        if (tb) {
          void markEdited([id])
          enqueueUpdate(buildStowedElement(tb, undefined, projection), {
            operation: 'update',
            meta: { historyTxnId: txnId },
          })
        }
      }

      void markEdited([rm.shape.id, ...releasedNonGroupIds, ...nestedChildGroupIds])
      enqueueDelete(rm.shape.id, { meta: { historyTxnId: txnId } })
    } else {
      let apiBody: UngroupElementsRequest | undefined

      if (releasedNonGroupIds.length > 0) {
        try {
          apiBody = buildUngroupElementsApiBody(releasedNonGroupIds, shapes, textBoxes, projection)
        } catch (e) {
          warn('persistRedoUngroupLikeExplicit: failed to build ungroup API body', e)
          continue
        }
      }

      enqueueUngroup({
        groupId: rm.shape.id,
        parentGroupId: rm.shape.parentGroupId,
        childIds: releasedNonGroupIds,
        insertIndex: rm.historyInsertIndex,
        meta: { historyTxnId: txnId },
        ...(apiBody && apiBody.length > 0 ? { apiBody } : {}),
      })
    }
  }
}
